import React, { useEffect, useRef, useState } from 'react';
import { type ColorResult, ChromePicker } from 'react-color';
import styled, { useTheme } from 'styled-components';
import { ExternalActivityListener, GetColor, RelativePortal } from 'venn-ui-kit';

const ColorPickerElement = ({ color, onChange }: { color: string; onChange: (color: string) => void }) => {
  const { Colors } = useTheme();
  const [editing, setEditing] = useState(false);
  const [hexColor, setHexColor] = useState<string>(color);

  const onColorSelect = (hexColor: string) => {
    setHexColor(hexColor);
    onChange(hexColor);
  };

  const portalClassName = 'color-picker-portal';

  return (
    <span className="inline-flex items-center h-[12px] relative" data-testid="qa-selected-color">
      <ExternalActivityListener
        listeningEnabled={editing}
        onExternalActivity={() => setEditing(false)}
        ignoreActivityFromClassName={portalClassName}
      >
        <ColorPatch aria-label={hexColor} color={hexColor} onClick={() => setEditing(true)} highlighted={editing} />
      </ExternalActivityListener>
      {editing && (
        <RelativePortal expectedHeight={260} leftOffset={-40} topOffset={15} className={portalClassName}>
          <ColorSwatch color={hexColor} onChange={onColorSelect} />
        </RelativePortal>
      )}
      <span style={{ color: Colors.GreyScale.Grey80, paddingLeft: '4px' }}>{hexColor}</span>
    </span>
  );
};

const ColorSwatch = ({ color, onChange }: { color: string; onChange: (newColor: string) => void }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onChangeComplete = (newColor: ColorResult) => {
    const hexColor = newColor.hex.toUpperCase();
    onChange(hexColor);
  };

  useEffect(() => {
    const hexInput = wrapperRef.current?.querySelector('input');
    hexInput?.focus();
    hexInput?.select();
  }, []);

  return (
    <div ref={wrapperRef}>
      <ChromePicker color={color} onChangeComplete={onChangeComplete} />
    </div>
  );
};

export const ColorPicker = ({
  color,
  onChange,
  children,
}: {
  color: string;
  onChange: (color: string) => void;
  children?: React.ReactNode;
}) => {
  return (
    <span className="inline-flex items-center h-[12px] relative text-[12px]">
      {children}
      <ColorPickerElement color={color} onChange={onChange} />
    </span>
  );
};

const ColorPatch = styled.button<{ color: string; highlighted: boolean }>`
  width: 40px;
  height: 16px;
  border-radius: 2px;
  background-color: ${(props) => props.color};
  border: 1px solid ${({ highlighted }) => (highlighted ? GetColor.Primary.Main : GetColor.GreyScale.Grey30)};
  margin-left: 5px;
`;
